/**
 * HeatmapShareModal (Iter 31)
 *
 * Two-stage modal:
 *   stage 1 ("form")    → recipient + TTL + include-AI-narrative toggle.
 *   stage 2 ("success") → renders the freshly minted share URL with a
 *                          one-click Copy-to-Clipboard button.
 *
 * Lives over the Heat Map sub-tab. The list of active shares is rendered
 * elsewhere (HeatmapSharesList) so a consultant doesn't have to dismiss
 * this modal to find old links.
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  XIcon as X,
  ShareNetworkIcon as ShareNetwork,
  CopyIcon as Copy,
  CheckIcon as Check,
  LinkIcon as LinkIcon,
  WarningOctagonIcon as WarningOctagon,
} from "@phosphor-icons/react";
import { createHeatmapShare } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";

const TTL_PRESETS = [
  { days: 7,   label: "7 days" },
  { days: 30,  label: "30 days" },
  { days: 90,  label: "90 days" },
  { days: 365, label: "1 year" },
];

export default function HeatmapShareModal({
  workspaceId,
  open,
  onClose,
  onCreated,
  deliverableId,       // optional — set if a Claude narrative should be embedded
  hasReadyDeliverable, // bool — disables the include-AI toggle when no narrative exists
}) {
  const [stage, setStage] = useState("form");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientLabel, setRecipientLabel] = useState("");
  const [ttlDays, setTtlDays] = useState(30);
  const [includeAi, setIncludeAi] = useState(Boolean(deliverableId && hasReadyDeliverable));
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const fullShareUrl = created
    ? `${window.location.origin}/share/heatmap/${created.token}`
    : "";

  const reset = () => {
    setStage("form");
    setRecipientEmail("");
    setRecipientLabel("");
    setTtlDays(30);
    setIncludeAi(Boolean(deliverableId && hasReadyDeliverable));
    setCreated(null);
    setCopied(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const res = await createHeatmapShare(workspaceId, {
        recipientEmail: recipientEmail.trim() || undefined,
        recipientLabel: recipientLabel.trim() || undefined,
        ttlDays,
        deliverableId: includeAi && deliverableId ? deliverableId : undefined,
      });
      setCreated(res);
      setStage("success");
      onCreated?.(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to create share link");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullShareUrl);
      setCopied(true);
      toast.success("Share URL copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't access clipboard — copy the URL manually.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="share-modal-backdrop"
      onClick={close}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white border border-[var(--ml-border)] max-w-lg w-full shadow-xl"
        data-testid="share-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--ml-border)]">
          <div className="flex items-center gap-2">
            <ShareNetwork size={16} weight="bold" className="text-[var(--ml-primary)]" />
            <h3 className="font-semibold text-lg">
              {stage === "form" ? "Share heat map" : "Share link ready"}
            </h3>
          </div>
          <button
            type="button"
            onClick={close}
            data-testid="share-modal-close"
            className="ml-btn-icon text-[var(--ml-text-secondary)] hover:text-[var(--ml-text)]"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {stage === "form" && (
          <div className="p-5 space-y-4" data-testid="share-modal-form">
            <p className="text-sm text-[var(--ml-text-secondary)]">
              Mint a public read-only link to this heat map. Anyone with the URL
              can view it — no login required. Use a recipient email to watermark
              the PDF and trace any leak back to the recipient.
            </p>

            <div>
              <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1">
                Recipient email <span className="opacity-50 ml-mono">(optional, watermarks the PDF)</span>
              </label>
              <input
                type="email"
                placeholder="jane.cio@customer.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="ml-input w-full"
                data-testid="share-modal-email"
              />
            </div>

            <div>
              <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1">
                Recipient label <span className="opacity-50 ml-mono">(optional, for your tracking)</span>
              </label>
              <input
                type="text"
                placeholder="Jane @ NA Bank — discovery review"
                value={recipientLabel}
                onChange={(e) => setRecipientLabel(e.target.value)}
                className="ml-input w-full"
                data-testid="share-modal-label"
              />
            </div>

            <div>
              <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1">
                Expires after
              </label>
              <div className="flex gap-1 flex-wrap" role="radiogroup">
                {TTL_PRESETS.map((p) => (
                  <button
                    key={p.days}
                    type="button"
                    onClick={() => setTtlDays(p.days)}
                    data-testid={`share-modal-ttl-${p.days}`}
                    className={`px-3 py-1.5 text-sm border ml-mono ${ttlDays === p.days ? "bg-[var(--ml-primary)] text-white border-[var(--ml-primary)]" : "border-[var(--ml-border)] hover:bg-[var(--ml-surface)]/40"}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {deliverableId && (
              <label
                className={`flex items-start gap-2 text-sm cursor-pointer ${hasReadyDeliverable ? "" : "opacity-50 cursor-not-allowed"}`}
              >
                <input
                  type="checkbox"
                  checked={includeAi}
                  disabled={!hasReadyDeliverable}
                  onChange={(e) => setIncludeAi(e.target.checked)}
                  className="mt-1"
                  data-testid="share-modal-include-ai"
                />
                <span>
                  <strong>Include AI narrative</strong> from the most recent Claude-generated
                  heat-map report.
                  {!hasReadyDeliverable && (
                    <span className="block text-xs text-[var(--ml-text-secondary)] mt-0.5">
                      <WarningOctagon size={10} weight="bold" className="inline mr-1" />
                      No ready deliverable found — generate one first.
                    </span>
                  )}
                </span>
              </label>
            )}

            <div className="flex justify-end gap-2 border-t border-[var(--ml-border)] -mx-5 px-5 pt-4">
              <button type="button" onClick={close} className="ml-btn-secondary" data-testid="share-modal-cancel">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={submitting}
                data-testid="share-modal-create-btn"
                className="ml-btn-primary"
              >
                <LinkIcon size={14} weight="bold" /> {submitting ? "Creating…" : "Create share link"}
              </button>
            </div>
          </div>
        )}

        {stage === "success" && created && (
          <div className="p-5 space-y-4" data-testid="share-modal-success">
            <p className="text-sm text-[var(--ml-text-secondary)]">
              Send this URL to your recipient. It works without a Modernization
              Lab login and expires on{" "}
              <strong>{new Date(created.expires_at).toLocaleDateString()}</strong>.
              You can revoke it any time from the Active shares list.
            </p>

            <div className="ml-card p-3 bg-[var(--ml-surface)]/40">
              <code
                className="ml-mono text-xs break-all block"
                data-testid="share-modal-url"
              >
                {fullShareUrl}
              </code>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCopy}
                data-testid="share-modal-copy-btn"
                className="ml-btn-primary flex-1"
              >
                {copied ? <><Check size={14} weight="bold" /> Copied!</> : <><Copy size={14} weight="bold" /> Copy URL</>}
              </button>
              <a
                href={fullShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-btn-secondary"
                data-testid="share-modal-preview-link"
              >
                Open preview
              </a>
            </div>

            <button
              type="button"
              onClick={close}
              className="ml-btn-ghost w-full mt-2"
              data-testid="share-modal-done"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
